import React, { useEffect, useState } from 'react'
import * as styles from './StickyAnchorNav.module.scss'

interface StickyAnchorNavProps {
  links: { title: string; href: string }[]
}

const StickyAnchorNav: React.FC<StickyAnchorNavProps> = ({ links }) => {
  const [isActive, setIsActive] = useState(false)
  const [activeAnchor, setActiveAnchor] = useState<string | null>(null)

  useEffect(() => {
    const sections = links.map((link) => document.querySelector(link.href))
    if (sections.some((section) => !section)) return

    const handleScroll = () => {
      const firstSection = sections[0]
      const lastSection = sections[sections.length - 1]
      const firstRect = firstSection?.getBoundingClientRect()
      const lastRect = lastSection?.getBoundingClientRect()

      const isFirstAtBottom = firstRect?.bottom <= window.innerHeight
      const isLastAtBottom = lastRect?.bottom <= window.innerHeight

      if (isFirstAtBottom && !isLastAtBottom) {
        setIsActive(true)
      } else if (isLastAtBottom) {
        setIsActive(false)
      } else if (!isFirstAtBottom) {
        setIsActive(false)
      }

      // Determine the active anchor closest to the center of the viewport
      let closestSection: Element | null = null
      let closestDistance = Number.POSITIVE_INFINITY

      sections.forEach((section) => {
        const rect = section?.getBoundingClientRect()
        if (rect) {
          const distance = Math.abs(rect.top + rect.height / 2 - window.innerHeight / 2)
          if (distance < closestDistance) {
            closestDistance = distance
            closestSection = section
          }
        }
      })

      if (closestSection) {
        setActiveAnchor(`#${closestSection.id}`)
      }
    }

    window.addEventListener('scroll', handleScroll)
    return () => window.removeEventListener('scroll', handleScroll)
  }, [links])

  const handleClick = (event: React.MouseEvent<HTMLAnchorElement>, href: string) => {
    event.preventDefault()
    const target = document.querySelector(href)
    if (target) {
      const targetRect = target.getBoundingClientRect()
      const scrollOffset = targetRect.top + window.scrollY - window.innerHeight / 2 + targetRect.height / 2
      window.scrollTo({ top: scrollOffset, behavior: 'smooth' })
    }
  }

  return (
    <nav className={`${styles.nav} ${isActive ? styles.active : ''}`}>
      <ul className={styles.navList}>
        {links.map((link) => (
          <li key={link.href}>
            <a
              href={link.href}
              className={`${styles.navLink} ${activeAnchor === link.href ? styles.activeLink : ''}`}
              onClick={(event) => handleClick(event, link.href)}
            >
              {link.title}
            </a>
          </li>
        ))}
      </ul>
    </nav>
  )
}

export default StickyAnchorNav